import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet';
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Paper,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { styled } from '@mui/material/styles';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QRCode from 'react-qr-code';

import TikTokIcon from '../Components/Icons/TikTok';
import TwitchIcon from '../Components/Icons/Twitch';
import CastIcon from '@mui/icons-material/Cast'; // Kick
import PodcastsIcon from '@mui/icons-material/Podcasts';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkIcon from '@mui/icons-material/Link';

import API from '../API';
import HomeHeader from '../Components/HomeHeader';
import { lightTheme } from '../Utils/baseTheme';
import defaultPfp from '../Components/globalAssets/ppfLogo.png';

/*********************************************************************
 * Rainbow “Let’s Chat” button
 *********************************************************************/
const BookButton = styled('button')(({ theme }) => ({
  background: 'linear-gradient(45deg, red, orange, yellow, green, blue, indigo, violet)',
  backgroundSize: '400% 400%',
  color: '#FFF',
  border: 'none',
  borderRadius: '23px',
  fontWeight: 700,
  height: 46,
  width: 140,
  cursor: 'pointer',
  transition: 'transform 0.3s ease-in-out',
  animation: 'rainbow 6s ease infinite',
  marginTop: theme.spacing(2),
  '&:hover': { transform: 'scale(1.1)' },
  '@keyframes rainbow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  }
}));

/*********************************************************************
 * Hero layout + helpers
 *********************************************************************/
const HeroSection = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  minHeight: 400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden'
}));
const HeroImageBackground = styled('div')(({ imageUrl }) => ({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${imageUrl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  zIndex: 0
}));
const HeroVideo = styled('video')(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 0
}));
const HeroOverlay = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.3)',
  backdropFilter: 'blur(2px)',
  zIndex: 1
}));
const HeroContent = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  color: '#fff',
  padding: theme.spacing(2)
}));
const HeroAvatar = styled('img')(({ theme }) => ({
  width: 160,
  height: 160,
  borderRadius: '5%',
  objectFit: 'cover',
  backgroundColor: '#fff',
  marginBottom: theme.spacing(2)
}));

/*********************************************************************
 * Items grid + card
 *********************************************************************/
const ItemsContainer = styled(Paper)(({ theme }) => ({
  position: 'relative',
  margin: theme.spacing(3, 'auto'),
  padding: theme.spacing(2),
  maxWidth: 600,
  width: '100%',
  borderRadius: theme.shape.borderRadius
}));
const ActionCard = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  cursor: 'pointer',
  boxShadow: theme.shadows[3],
  '&:hover .itemOverlay': { opacity: 1 }
}));
const ItemMedia = styled('div')(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));
const Overlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0,0,0,0.4)',
  backdropFilter: 'blur(6px)',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  textAlign: 'center',
  padding: theme.spacing(2),
  color: '#fff'
}));
const Watermark = styled('div')(({ theme }) => ({
  textAlign: 'center',
  opacity: 0.5,
  padding: theme.spacing(2)
}));

/******************** vCard builder ************************/
function buildVCard(contact) {
  let lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${contact.full_name}`
  ];
  if (contact.first_name || contact.last_name) {
    lines.push(
      `N:${contact.last_name || ''};${contact.first_name || ''};${contact.middle_name || ''};${contact.nickname || ''}`
    );
  }
  if (contact.email) lines.push(`EMAIL;TYPE=INTERNET:${contact.email}`);
  if (contact.phone) lines.push(`TEL;TYPE=CELL:${contact.phone}`);
  if (contact.company) lines.push(`ORG:${contact.company}`);
  if (contact.title) lines.push(`TITLE:${contact.title}`);
  lines.push('END:VCARD');
  return lines.join('\n');
}

export default function PublicBioPage() {
  const { slug } = useParams();
  const [qrOpen, setQrOpen] = useState(false);

  /* ─────────────────── Fetch page + creator data ─────────────────── */
  const { data: bioPage, isLoading: bioLoading, error: bioErr } = useQuery(
    ['bioPage', slug],
    () => API.bio.getPublic(slug),
    { enabled: !!slug, refetchOnWindowFocus: false }
  );
  const { data: creatorResp, isLoading: creatorLoading, error: creErr } = useQuery(
    ['creatorInfo', slug],
    () => API.creators.fetchDetails(slug),
    { enabled: !!slug, refetchOnWindowFocus: false }
  );

  const loading = bioLoading || creatorLoading;
  const error = bioErr || creErr;

  if (loading) {
    return (
      <Backdrop open>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }
  if (error) {
    return (
      <Box p={4}><Typography color="error">{error.message || 'Error loading data.'}</Typography></Box>
    );
  }
  if (!bioPage || !creatorResp) {
    return (
      <Box p={4}><Typography textAlign="center">This bio page or creator does not exist.</Typography></Box>
    );
  }

  /* ───────────── Extract page + creator info ───────────── */
  const { title, description, header_media_url, items = [], qr_enabled, self_contact } = bioPage;
  const {
    display_name,
    pfphref,
    tiktok_link,
    instagram_link,
    youtube_link,
    twitter_link,
    twitch_link,
    snap_link,
    x_link,
    kick_link,
    podcast_link,
    creator: creatorUsername
  } = creatorResp.creator;

  const isHeaderImage = header_media_url && /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(header_media_url);
  const sorted = [...items].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  const small = sorted.filter(i => i.size === 'SMALL');
  const med   = sorted.filter(i => i.size === 'MEDIUM');
  const large = sorted.filter(i => i.size === 'LARGE');

  /* ───────────── Render helper ───────────── */
  const renderItem = item => {
    if (item.component_type === 'VIDEO_EMBED' && item.url) {
      return (
        <Grid item xs={12} key={item.id}>
          <ActionCard onClick={() => window.open(item.url, '_blank')}>
            <Box sx={{ position:'relative', width:'100%', paddingBottom:'56.25%' }}>
              <iframe title={item.label||'video'} src={item.url} width="100%" height="100%" style={{border:'none',position:'absolute',top:0,left:0}} allowFullScreen />
            </Box>
            <Overlay className="itemOverlay">
              {item.label && <Typography variant="h6">{item.label}</Typography>}
              {item.description && <Typography variant="body2" sx={{mt:1}}>{item.description}</Typography>}
            </Overlay>
          </ActionCard>
        </Grid>
      );
    }
    const cols = item.size === 'LARGE' ? 12 : item.size === 'MEDIUM' ? 6 : 4;
    return (
      <Grid item xs={cols} key={item.id}>
        <ActionCard onClick={() => item.url && window.open(item.url,'_blank')}>
          <ItemMedia>
            {item.media_url ? (
              /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.media_url) ? (
                <Box component="img" src={item.media_url} alt={item.label||'img'} sx={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
              ) : (
                <video src={item.media_url} autoPlay loop muted style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
              )
            ) : (
              <Box sx={{width:'100%',height:200,bgcolor:'grey.300',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Typography variant="overline">No Media</Typography>
              </Box>
            )}
          </ItemMedia>
          <Overlay className="itemOverlay">
            {item.label && <Typography variant="h6">{item.label}</Typography>}
            {item.description && <Typography variant="body2" sx={{mt:1}}>{item.description}</Typography>}
          </Overlay>
        </ActionCard>
      </Grid>
    );
  };

  /* ───────────── vCard + QR helpers ───────────── */
  const vcardStr  = self_contact ? buildVCard(self_contact) : '';
  const vcardData = `data:text/vcard;charset=utf-8,${encodeURIComponent(vcardStr)}`;

  return (
    <>
      <Helmet><title>{title || display_name || slug}</title></Helmet>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Toolbar />

        {/* ───────────── HERO ───────────── */}
        <HeroSection>
          {header_media_url ? (
            isHeaderImage ? <HeroImageBackground imageUrl={header_media_url} /> : <HeroVideo src={header_media_url} autoPlay muted loop />
          ) : <Box sx={{position:'absolute',inset:0,bgcolor:'#444'}} />}
          <HeroOverlay />
          <HeroContent>
            {!isHeaderImage && <HeroAvatar src={pfphref || defaultPfp} alt={display_name || slug} />}
            <Typography variant="h3" sx={{fontWeight:700}}>{title || display_name || slug}</Typography>
            {description && <Typography variant="h6" sx={{mt:1,maxWidth:'80%',mx:'auto'}}>{description}</Typography>}

            {/* Social icons row */}
            <Box sx={{display:'flex',flexWrap:'wrap',justifyContent:'center',gap:1.5,mt:2}}>
              {tiktok_link   && <Tooltip title="TikTok"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(tiktok_link,'_blank')}><TikTokIcon/></IconButton></Tooltip>}
              {instagram_link&& <Tooltip title="Instagram"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(instagram_link,'_blank')}><InstagramIcon/></IconButton></Tooltip>}
              {youtube_link  && <Tooltip title="YouTube"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(youtube_link,'_blank')}><YouTubeIcon/></IconButton></Tooltip>}
              {twitter_link  && <Tooltip title="Twitter"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(twitter_link,'_blank')}><TwitterIcon/></IconButton></Tooltip>}
              {twitch_link   && <Tooltip title="Twitch"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(twitch_link,'_blank')}><TwitchIcon/></IconButton></Tooltip>}
              {snap_link     && <Tooltip title="Snapchat"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(snap_link,'_blank')}><LinkIcon/></IconButton></Tooltip>}
              {x_link        && <Tooltip title="X"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(x_link,'_blank')}><TwitterIcon/></IconButton></Tooltip>}
              {kick_link     && <Tooltip title="Kick"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(kick_link,'_blank')}><CastIcon/></IconButton></Tooltip>}
              {podcast_link  && <Tooltip title="Podcast"><IconButton sx={{color:'#fff'}} onClick={()=>window.open(podcast_link,'_blank')}><PodcastsIcon/></IconButton></Tooltip>}
            </Box>

            {/* Let’s Chat button */}
            <Box textAlign="center"><BookButton onClick={()=>window.open(`/creators/${creatorUsername}`,'_self')}>Let's Chat</BookButton></Box>

            {/* Save‑contact QR button */}
            {qr_enabled && self_contact && (
              <Box mt={2} textAlign="center">
                <Button variant="outlined" startIcon={<QrCodeIcon />} onClick={()=>setQrOpen(true)}>Save My Info</Button>
              </Box>
            )}
          </HeroContent>
        </HeroSection>

        {/* ───────────── Links Section ───────────── */}
        <ItemsContainer elevation={6}>
          <Typography variant="h5" gutterBottom>My Links</Typography>
          <Divider sx={{mb:3}} />
          <Grid container spacing={2} sx={{mb:2}}>{large.map(renderItem)}</Grid>
          <Grid container spacing={2} sx={{mb:2}}>{med.map(renderItem)}</Grid>
          <Grid container spacing={2}>{small.map(renderItem)}</Grid>
          <Watermark><Typography variant="body2">Powered by Blitz</Typography></Watermark>
        </ItemsContainer>

        {/* ───────────── QR / vCard Dialog ───────────── */}
        <Dialog open={qrOpen} onClose={()=>setQrOpen(false)}>
          <DialogTitle>Scan to add contact</DialogTitle>
          <DialogContent sx={{display:'flex',flexDirection:'column',alignItems:'center'}}>
            <QRCode value={vcardData} size={220} />
            <Button sx={{mt:2}} variant="contained" href={vcardData} download={`${self_contact?.full_name||'contact'}.vcf`}>Download vCard</Button>
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    </>
  );
}
